import { formatGiftBalance } from '../../../../components/horasRegalo/giftBalance';
import {useSpaceAvailability} from '../../../../hooks/useWebSocket';
// ReservaYa.tsx — Precio corregido: multiplica por cantidad de períodos
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DateRange } from 'react-day-picker';
import { format, addHours, parse } from 'date-fns';
import {
    MapPin, Clock, Users, ChevronRight, ChevronLeft, CheckCircle2,
    Calendar, ArrowRight, Building2, Package, Phone, Percent, X, Gift,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    fetchCategories, fetchServiciosByCategory, fetchPrecioPlanesServicios,
    fetchEspacioServicios, PrecioPlanServicio, Servicio, Categoria,
    EspacioServicio,
    fetchPlanDuracionConfigs, PlanDuracionConfig,
    validarCuponPorCodigo, mensajeErrorCupon, CuponValidado,
} from '../../../../services/landing/citaslandingService';
import { fetchSucursales, Sucursal } from '../../../../services/pos/sucursalService';
import { fetchEspaciosDisponiblesPorServicio } from '../../../../services/espacioServiciosService';
import { crearSolicitudReserva, SolicitudReserva } from '../../../../services/solicitudReservaService';
import ReservationRequestSuccess from './ReservationRequestSuccess';
import { useCouponValidation } from '../../../../components/booking/useCouponValidation';
import { useAuth } from '../../../../context/AuthContext';
import { ROUTES } from '../../../../routes/routePaths';
import { useMonthlySpaceOccupancy } from '../../../../components/booking/useMonthlySpaceOccupancy';
import './reservaYa.css';
import SpaceDaySchedule from '../../../../components/booking/SpaceDaySchedule';
import { bookingCalendarTokens } from '../../../../components/booking/bookingCalendarTokens';
import { fetchMiAfiliacion, Afiliacion } from '../../../../services/afiliadosService';

// ─── Helpers ──────────────────────────────────────────────────
function fmtDateWithoutTZ(date: Date): string {
    const y = date.getFullYear(), m = String(date.getMonth() + 1).padStart(2, '0'), d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
export function reservationDates(startKey: string, count: number, businessOnly = false): string[] {
    if (!startKey) return [];
    const date = parseLocalDate(startKey);
    const dates: string[] = [];
    const total = Math.max(1, Math.floor(count) || 1);
    while (dates.length < total) {
        if (!businessOnly || (date.getDay() !== 0 && date.getDay() !== 6)) dates.push(fmtDateWithoutTZ(date));
        date.setDate(date.getDate() + 1);
    }
    return dates;
}
// Crea un Date a partir de string YYYY-MM-DD sin zona horaria (local)
function parseLocalDate(dateStr: string): Date {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
}

export const availabilityMatchesContext = (
    validatedContext: string | null,
    currentContext: string | null,
    available: boolean | undefined,
) => !!currentContext && validatedContext === currentContext && available === true;

export const canSubmitSpaceReservation = (
    requiresSpace: boolean,
    hasSelectedSpace: boolean,
    validatedContext: string | null,
    currentContext: string | null,
    available: boolean | undefined,
) => !requiresSpace || (
    hasSelectedSpace
    && availabilityMatchesContext(validatedContext, currentContext, available)
);

const STEPS = ['Sucursal', 'Espacio y plan', 'Fecha y hora', 'Confirmar'];

// ─── MiniCalendar ─────────────────────────────────────────────
interface MiniCalendarProps {
    selected: string;
    selectedDates?: Set<string>;
    businessOnly?: boolean;
    onSelect: (d: string) => void;
    occupiedDates?: Set<string>;
    onMonthChange?: (month: string) => void;
    initialMonth?: Date;
}
export function MiniCalendar({ selected, selectedDates, businessOnly = false, onSelect, occupiedDates = new Set(), onMonthChange, initialMonth }: MiniCalendarProps) {
    const today = new Date();
    const initial = initialMonth || today;
    const [vm, setVm] = useState(initial.getMonth());
    const [vy, setVy] = useState(initial.getFullYear());
    const initialYear = initialMonth?.getFullYear();
    const initialMonthNumber = initialMonth?.getMonth();
    useEffect(() => {
        if (initialYear !== undefined && initialMonthNumber !== undefined) {
            setVy(initialYear); setVm(initialMonthNumber);
        }
    }, [initialYear, initialMonthNumber]);
    const cells: (number | null)[] = [...Array(new Date(vy, vm, 1).getDay()).fill(null), ...Array.from({ length: new Date(vy, vm + 1, 0).getDate() }, (_, i) => i + 1)];
    while (cells.length % 7 !== 0) cells.push(null);
    const toKey = (d: number) => `${vy}-${String(vm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const isPast = (d: number) => new Date(vy, vm, d) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const moveMonth = (delta: number) => {
        const next = new Date(vy, vm + delta, 1);
        setVm(next.getMonth()); setVy(next.getFullYear());
        onMonthChange?.(format(next, 'yyyy-MM'));
    };
    return (
        <div className="booking-calendar">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <button aria-label="Mes anterior" onClick={() => moveMonth(-1)} style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1.5px solid #f1f5f9', background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .15s' }}
                    onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9'}
                    onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc'}>
                    <ChevronLeft size={13} color="#64748b" />
                </button>
                <span style={{ fontSize: '.9rem', fontWeight: '900', color: '#0f172a', textTransform: 'capitalize', letterSpacing: '-.01em' }}>{new Date(vy, vm).toLocaleString('es', { month: 'long', year: 'numeric' })}</span>
                <button aria-label="Mes siguiente" onClick={() => moveMonth(1)} style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1.5px solid #f1f5f9', background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .15s' }}
                    onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9'}
                    onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc'}>
                    <ChevronRight size={13} color="#64748b" />
                </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px', marginBottom: '6px' }}>
                {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((d, i) => <div key={i} style={{ textAlign: 'center', fontSize: '.58rem', fontWeight: '800', color: '#94a3b8', padding: '3px 0', letterSpacing: '.06em' }}>{d}</div>)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px' }}>
                {cells.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const key = toKey(d), isSel = selectedDates ? selectedDates.has(key) : selected === key, past = isPast(d), weekend = [0, 6].includes(new Date(vy, vm, d).getDay()), disabled = past || (businessOnly && weekend), occupied = occupiedDates.has(key);
                    const defaultBackground = occupied ? bookingCalendarTokens.occupiedBackground : 'transparent';
                    const dateLabel = new Date(vy, vm, d).toLocaleDateString('es', { day: 'numeric', month: 'long' });
                    return (
                        <button key={i} aria-pressed={isSel} aria-label={`${dateLabel}${businessOnly && weekend ? ', fin de semana no incluido' : occupied ? ', con ocupación' : ', disponible'}`} onClick={() => !disabled && onSelect(key)} disabled={disabled}
                            style={{ position:'relative', width: '100%', minHeight:'40px', aspectRatio: '1', borderRadius: bookingCalendarTokens.radius, border: occupied ? `1.5px solid ${bookingCalendarTokens.occupiedBorder}` : '1.5px solid transparent', background: isSel ? bookingCalendarTokens.selected : defaultBackground, color: disabled ? '#d1d5db' : isSel ? '#fff' : occupied ? bookingCalendarTokens.occupiedText : '#374151', fontSize: '.75rem', fontWeight: isSel||occupied ? '800' : '500', cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all .12s', boxShadow: isSel ? `${occupied ? `inset 0 0 0 1px ${bookingCalendarTokens.occupiedBorder},` : ''}0 3px 10px ${bookingCalendarTokens.selectedShadow}` : occupied ? '0 2px 8px rgba(249,115,22,.12)' : 'none' }}
                            onMouseEnter={e => { if (!disabled && !isSel) (e.currentTarget as HTMLButtonElement).style.background = occupied ? bookingCalendarTokens.occupiedHover : `${bookingCalendarTokens.selected}12`; }}
                            onMouseLeave={e => { if (!isSel) (e.currentTarget as HTMLButtonElement).style.background = defaultBackground; }}
                        >{d}{occupied&&<span data-occupancy-marker="true" aria-hidden="true" style={{position:'absolute',bottom:'3px',left:'50%',transform:'translateX(-50%)',width:isSel?'6px':'5px',height:isSel?'6px':'5px',borderRadius:'50%',background:bookingCalendarTokens.occupiedDot,border:isSel?'1px solid #fff':'none'}}/>}</button>
                    );
                })}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:'7px',marginTop:'14px',paddingTop:'12px',borderTop:'1px solid #f1f5f9'}}>
                <span style={{width:'9px',height:'9px',borderRadius:'50%',background:bookingCalendarTokens.occupiedDot}}/>
                <span style={{fontSize:'.7rem',fontWeight:'800',color:'#9a3412'}}>Con ocupación</span>
                <span style={{fontSize:'.68rem',color:'#64748b'}}>Puedes seleccionarla para revisar horarios libres.</span>
            </div>
        </div>
    );
}

const ReservaYa: React.FC = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submittedRequest, setSubmittedRequest] = useState<SolicitudReserva | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const topRef = useRef<HTMLDivElement>(null);

    // Sucursales
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [selectedSucursal, setSelectedSucursal] = useState<Sucursal | null>(null);
    const [loadingSucursales, setLoadingSucursales] = useState(false);

    // Catálogo
    const [categorias, setCategorias] = useState<Categoria[]>([]);
    const [servicios, setServicios] = useState<Servicio[]>([]);
    const [planes, setPlanes] = useState<PrecioPlanServicio[]>([]);
    const [espacios, setEspacios] = useState<EspacioServicio[]>([]);
    const [selectedCategoria, setSelectedCategoria] = useState<Categoria | null>(null);
    const [selectedServicio, setSelectedServicio] = useState<Servicio | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<PrecioPlanServicio | null>(null);
    const [selectedEspacio, setSelectedEspacio] = useState<EspacioServicio | null>(null);
    const [calendarMonth, setCalendarMonth] = useState(format(new Date(), 'yyyy-MM'));
    const monthlyOccupancy = useMonthlySpaceOccupancy(selectedEspacio?.id, calendarMonth);

    // Plan configs (solo para mostrar duración y temporizador)
    const [planConfigs, setPlanConfigs] = useState<PlanDuracionConfig[]>([]);
    const [usaTemporizador, setUsaTemporizador] = useState(false);

    // Fechas / horas
    const [selectedDate, setSelectedDate] = useState('');
    const [range, setRange] = useState<DateRange | undefined>(undefined);
    const [horaInicio, setHoraInicio] = useState<string | null>(null);
    const [horaFin, setHoraFin] = useState<string | null>(null);
    const [cantidadPeriodos, setCantidadPeriodos] = useState<number>(1);
    const [daySelectionType, setDaySelectionType] = useState<'all' | 'business'>('all');
    const [isHoraFinManuallyModified, setIsHoraFinManuallyModified] = useState(false);
    const [persons, setPersons] = useState(1);

    // Precio (ahora se recalcula según cantidadPeriodos)
    const [totalPrice, setTotalPrice] = useState(0);

    // Disponibilidad espacios
    const [espaciosDisponibles, setEspaciosDisponibles] = useState<{ [k: number]: boolean }>({});
    const [availabilityError, setAvailabilityError] = useState('');
    const [validatedAvailabilityContext, setValidatedAvailabilityContext] = useState<string | null>(null);
    const availabilityRequestRef = useRef(0);
    const mountedRef = useRef(true);
    const categoryRequestRef = useRef(0);
    const serviceRequestRef = useRef(0);
    const planRequestRef = useRef(0);
    const submitRequestRef = useRef(0);
    const intentRef = useRef({ identity: '', generation: 0 });
    const scopeRef = useRef({ sucursalId: null as number | null, categoriaId: null as number | null, servicioId: null as number | null });
    useEffect(() => {
        // React 18 ejecuta setup → cleanup → setup en StrictMode de desarrollo.
        // Restablecer el flag en cada setup evita que el segundo montaje quede
        // marcado como desmontado y deje el spinner de categorías para siempre.
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Cupones: validación ligada a código normalizado + plan + sucursal/servicio
    // + actor autenticado + generación + montado. Editar el código, cambiar el
    // contexto o cambiar de usuario invalida y limpia el cupón aplicado de
    // inmediato; una respuesta tardía nunca se aplica. ``validate`` y
    // ``errorMessage`` son referencias de módulo (estables).
    const { user: actor } = useAuth();
    const actorIdentity = actor ? String((actor as any).id ?? (actor as any).email ?? '') : '';
    scopeRef.current = {
        sucursalId: selectedSucursal?.id ?? null,
        categoriaId: selectedCategoria?.id ?? null,
        servicioId: selectedServicio?.id ?? null,
    };
    const invalidateReservationIntent = useCallback(() => {
        categoryRequestRef.current += 1;
        serviceRequestRef.current += 1;
        planRequestRef.current += 1;
        availabilityRequestRef.current += 1;
        submitRequestRef.current += 1;
    }, []);
    const selectSucursal = useCallback((sucursal: Sucursal) => { invalidateReservationIntent(); setSelectedSucursal(sucursal); }, [invalidateReservationIntent]);
    const selectCategoria = useCallback((categoria: Categoria) => { serviceRequestRef.current += 1; planRequestRef.current += 1; availabilityRequestRef.current += 1; submitRequestRef.current += 1; setSelectedCategoria(categoria); }, []);
    const selectServicio = useCallback((servicio: Servicio) => { planRequestRef.current += 1; availabilityRequestRef.current += 1; submitRequestRef.current += 1; setSelectedServicio(servicio); }, []);
    const selectPlan = useCallback((plan: PrecioPlanServicio) => { availabilityRequestRef.current += 1; submitRequestRef.current += 1; setSelectedPlan(plan); if(!plan.requiere_espacio)setSelectedEspacio(null); }, []);
    const {
        codigo: cuponCodigo, setCodigo: setCuponCodigo, aplicado: cuponAplicado,
        error: cuponError, validando: validandoCupon, validar: validarCupon, limpiar: limpiarCupon,
    } = useCouponValidation<CuponValidado>({
        validate: validarCuponPorCodigo,
        errorMessage: mensajeErrorCupon,
        planId: selectedPlan?.id ?? null,
        context: `${selectedSucursal?.id ?? ''}|${selectedServicio?.id ?? ''}|${actorIdentity}`,
    });

    const currentIntentIdentity = [
        actorIdentity, selectedSucursal?.id ?? '', selectedCategoria?.id ?? '',
        selectedServicio?.id ?? '', selectedPlan?.id ?? '', selectedEspacio?.id ?? '',
        selectedDate, horaInicio ?? '', horaFin ?? '', range?.to ? fmtDateWithoutTZ(range.to) : '',
        cantidadPeriodos, daySelectionType, cuponAplicado?.id ?? '',
    ].join('|');
    if (intentRef.current.identity !== currentIntentIdentity) {
        intentRef.current = { identity: currentIntentIdentity, generation: intentRef.current.generation + 1 };
    }

    // Beneficio Afiliado (8 Horas Gratuitas)
    const [miAfiliacion, setMiAfiliacion] = useState<Afiliacion | null>(null);
    const [usarHorasAfiliado, setUsarHorasAfiliado] = useState(false);

    // UI
    const [loadingCat, setLoadingCat] = useState(false);
    const [loadingServ, setLoadingServ] = useState(false);
    const [loadingPlanes, setLoadingPlanes] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const scrollTop = () => {
        const target = topRef.current;
        if (target && typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Cargar beneficio de afiliado del usuario autenticado si existe
    useEffect(() => {
        const checkAfiliacion = async () => {
            try {
                const af = await fetchMiAfiliacion();
                if (af && (af.estado === 'activo' || af.paso7_kardex_horas_abierto) && Number(af.horas_gratuitas_disponibles) > 0) {
                    setMiAfiliacion(af);
                }
            } catch {
                // No autenticado o sin afiliación
            }
        };
        checkAfiliacion();
    }, []);

    // ── Efectos de carga ──
    useEffect(() => {
        setLoadingSucursales(true);
        fetchSucursales().then(d => setSucursales(d.filter(s => s.activo)))
            .catch(() => setError('Error al cargar sucursales'))
            .finally(() => setLoadingSucursales(false));
    }, []);

    useEffect(() => {
        fetchPlanDuracionConfigs().then(c => setPlanConfigs(c.filter(x => x.estado))).catch(console.error);
    }, []);


    useEffect(() => {
        const requestId = ++categoryRequestRef.current;
        if (!selectedSucursal) {
            setCategorias([]); setLoadingCat(false); return;
        }
        const sucursalId = selectedSucursal.id;
        setCategorias([]);
        setServicios([]); setPlanes([]); setEspacios([]);
        setSelectedCategoria(null); setSelectedServicio(null); setSelectedPlan(null); setSelectedEspacio(null);
        setSelectedDate(''); setRange(undefined); setHoraInicio(null); setHoraFin(null); setCantidadPeriodos(1);
        setLoadingCat(true);
        fetchCategories(sucursalId).then(data => {
            if (!mountedRef.current || requestId !== categoryRequestRef.current || scopeRef.current.sucursalId !== sucursalId) return;
            setCategorias(data);
        }).catch(() => {
            if (!mountedRef.current || requestId !== categoryRequestRef.current || scopeRef.current.sucursalId !== sucursalId) return;
            setError('No se pudieron cargar categorías');
        }).finally(() => {
            if (mountedRef.current && requestId === categoryRequestRef.current && scopeRef.current.sucursalId === sucursalId) setLoadingCat(false);
        });
    }, [selectedSucursal]);

    useEffect(() => {
        const requestId = ++serviceRequestRef.current;
        if (!selectedCategoria || !selectedSucursal) { setServicios([]); setSelectedServicio(null); setLoadingServ(false); return; }
        const categoriaId = selectedCategoria.id;
        const sucursalId = selectedSucursal.id;
        setServicios([]); setPlanes([]); setEspacios([]);
        setLoadingServ(true); setSelectedServicio(null); setSelectedPlan(null); setSelectedEspacio(null);
        fetchServiciosByCategory(categoriaId, sucursalId).then(data => {
            if (!mountedRef.current || requestId !== serviceRequestRef.current || scopeRef.current.categoriaId !== categoriaId || scopeRef.current.sucursalId !== sucursalId) return;
            setServicios(data);
        }).catch(() => {
            if (!mountedRef.current || requestId !== serviceRequestRef.current || scopeRef.current.categoriaId !== categoriaId || scopeRef.current.sucursalId !== sucursalId) return;
            setError('Error al cargar servicios');
        }).finally(() => {
            if (mountedRef.current && requestId === serviceRequestRef.current && scopeRef.current.categoriaId === categoriaId && scopeRef.current.sucursalId === sucursalId) setLoadingServ(false);
        });
    }, [selectedCategoria, selectedSucursal]);

    useEffect(() => {
        const requestId = ++planRequestRef.current;
        if (!selectedServicio || !selectedSucursal) { setPlanes([]); setSelectedPlan(null); setEspacios([]); setSelectedEspacio(null); setLoadingPlanes(false); return; }
        const servicioId = selectedServicio.id;
        const sucursalId = selectedSucursal.id;
        const requiereEspacio = selectedServicio.requiere_espacio;
        setPlanes([]); setEspacios([]);
        setLoadingPlanes(true); setSelectedPlan(null); setSelectedEspacio(null);
        Promise.all([
            fetchPrecioPlanesServicios(),
            requiereEspacio ? fetchEspacioServicios(servicioId, sucursalId) : Promise.resolve([]),
        ]).then(([all, esps]) => {
            if (!mountedRef.current || requestId !== planRequestRef.current || scopeRef.current.servicioId !== servicioId || scopeRef.current.sucursalId !== sucursalId) return;
            setPlanes(all.filter(p => p.servicio === servicioId && p.estado));
            setEspacios(esps.filter(e => e.estado));
            setSelectedEspacio(null);
        }).catch(() => {
            if (!mountedRef.current || requestId !== planRequestRef.current || scopeRef.current.servicioId !== servicioId || scopeRef.current.sucursalId !== sucursalId) return;
            setError('Error al cargar planes');
        }).finally(() => {
            if (mountedRef.current && requestId === planRequestRef.current && scopeRef.current.servicioId === servicioId && scopeRef.current.sucursalId === sucursalId) setLoadingPlanes(false);
        });
    }, [selectedServicio, selectedSucursal]);

    // ── Precio: se actualiza cuando cambia el plan o la cantidad de períodos ──
    useEffect(() => {
        if (!selectedPlan) {
            setTotalPrice(0);
            return;
        }
        const cfg = planConfigs.find(c => c.id === selectedPlan.plan_duracion_config);
        let price = Number(selectedPlan.precio_inicial) || 0;
        // Si el plan es por días (unidad 'dia'), multiplicamos por cantidadPeriodos
        if (cfg?.unidad === 'dia') {
            price = price * cantidadPeriodos;
        }
        // Si el plan es por horas, podría tener otra lógica, pero la dejamos simple por ahora
        setTotalPrice(price);
        setUsaTemporizador(cfg?.usa_temporizador || false);
        if (cfg?.usa_temporizador) {
            setHoraFin(null);
            setIsHoraFinManuallyModified(false);
        }
    }, [selectedPlan, planConfigs, cantidadPeriodos]);

    // Resetear horaFin manual cuando cambia horaInicio
    useEffect(() => { setIsHoraFinManuallyModified(false); }, [horaInicio, selectedPlan]);

    // Ajustar personas a capacidad máxima
    useEffect(() => {
        if (selectedEspacio && persons > selectedEspacio.cantidad_personas)
            setPersons(selectedEspacio.cantidad_personas);
    }, [selectedEspacio, persons]);

    // Calcular rango de fechas según cantidad de periodos y tipo de días
    useEffect(() => {
        if (!selectedDate || cantidadPeriodos < 1) {
            setRange(undefined);
            return;
        }
        const start = parseLocalDate(selectedDate);
        const cfg = selectedPlan ? planConfigs.find(c => c.id === selectedPlan.plan_duracion_config) : null;
        if (!cfg || cfg.unidad !== 'dia') {
            setRange({ from: start, to: start });
            return;
        }
        const dates = reservationDates(selectedDate, cantidadPeriodos * (cfg.dias || 1), daySelectionType === 'business');
        if (dates[0] !== selectedDate) {
            setSelectedDate(dates[0]);
            setCalendarMonth(dates[0].slice(0, 7));
            setHoraInicio(null); setHoraFin(null);
        }
        setRange({ from: parseLocalDate(dates[0]), to: parseLocalDate(dates[dates.length - 1]) });
    }, [selectedDate, cantidadPeriodos, daySelectionType, selectedPlan, planConfigs]);

    // Actualizar hora fin automática si no es temporizador
    useEffect(() => {
        if (!selectedPlan || usaTemporizador) return;
        const cfg = planConfigs.find(c => c.id === selectedPlan.plan_duracion_config);
        if (!cfg || !cfg.horas) return;
        if (horaInicio && !isHoraFinManuallyModified) {
            const startTime = parse(horaInicio, 'HH:mm', new Date());
            const endTime = addHours(startTime, cfg.horas);
            setHoraFin(format(endTime, 'HH:mm'));
        }
    }, [horaInicio, selectedPlan, planConfigs, usaTemporizador, isHoraFinManuallyModified]);

    const availabilityContext = selectedPlan?.requiere_espacio && selectedServicio && selectedDate && horaInicio
        ? [
            selectedServicio.id,
            selectedDate,
            horaInicio,
            horaFin || '23:59:59',
            range?.to ? fmtDateWithoutTZ(range.to) : selectedDate,
            daySelectionType,
        ].join('|')
        : null;
    const selectedAvailability = selectedEspacio && validatedAvailabilityContext === availabilityContext
        ? espaciosDisponibles[selectedEspacio.id]
        : undefined;

    // Verificar disponibilidad de espacios
    const verificarDisponibilidad = useCallback(async () => {
        const requestId = ++availabilityRequestRef.current;
        if (!availabilityContext || !selectedServicio) {
            setEspaciosDisponibles({});
            setValidatedAvailabilityContext(null);
            return;
        }
        setEspaciosDisponibles({});
        setValidatedAvailabilityContext(null);
        setAvailabilityError('');
        const fechaFin = range?.to ? fmtDateWithoutTZ(range.to) : selectedDate;
        try {
            const disponibles = await fetchEspaciosDisponiblesPorServicio(
                selectedServicio.id, selectedDate, horaInicio!, horaFin || '23:59:59', fechaFin,
                daySelectionType === 'business'
            );
            if (requestId !== availabilityRequestRef.current) return;
            const ids = new Set(disponibles.map(e => e.id));
            const disp: { [k: number]: boolean } = {};
            espacios.forEach(e => { disp[e.id] = ids.has(e.id); });
            setEspaciosDisponibles(disp);
            setValidatedAvailabilityContext(availabilityContext);
        } catch {
            if (requestId !== availabilityRequestRef.current) return;
            setAvailabilityError('No pudimos verificar este horario. Actualiza la consulta para continuar.');
        }
    }, [availabilityContext, selectedDate, horaInicio, horaFin, range?.to, espacios, selectedServicio, daySelectionType]);

    useSpaceAvailability(branchId => { if (!branchId || branchId === selectedSucursal?.id) void verificarDisponibilidad(); });
    useEffect(() => { verificarDisponibilidad(); }, [verificarDisponibilidad]);

    // Validar cupón: el cliente NUNCA descarga el catálogo de cupones; valida su
    // código exacto en el servidor (vigencia, plan aplicable y uso único se
    // comprueban allí) y la respuesta solo se aplica bajo la misma identidad.

    // Total con descuento
    const calcularTotalConDescuento = () => {
        let sub = Number(totalPrice) || 0, desc = 0;
        if (cuponAplicado) {
            if (cuponAplicado.descuento_monto) desc += Number(cuponAplicado.descuento_monto) || 0;
            else if (cuponAplicado.descuento_porcentaje) desc += (sub * (Number(cuponAplicado.descuento_porcentaje) || 0)) / 100;
        }
        return Math.max(sub - desc, 0);
    };
    const totalFinal = calcularTotalConDescuento();

    // canNext
    const canNext = (): boolean => {
        if (step === 0) return !!selectedSucursal;
        if (step === 1) return !!selectedPlan && (!selectedPlan.requiere_espacio || !!selectedEspacio);
        if (step >= 2) return !!selectedDate && !!horaInicio && (
            !selectedPlan?.requiere_espacio
            || canSubmitSpaceReservation(
                true,
                !!selectedEspacio,
                validatedAvailabilityContext,
                availabilityContext,
                selectedEspacio ? espaciosDisponibles[selectedEspacio.id] : undefined,
            )
        );
        return true;
    };

    // Formateadores
    const formatDate = (d: string) => {
        if (!d) return '';
        const [y, m, day] = d.split('-');
        return new Date(+y, +m - 1, +day).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' });
    };
    const getDuracion = (p: PrecioPlanServicio) => {
        const cfg = planConfigs.find(c => c.id === p.plan_duracion_config);
        if (cfg?.unidad === 'hora') return `${cfg.horas} hora(s) por sesión`;
        if (cfg?.unidad === 'dia') return `${cfg.dias} día(s) por período`;
        return p.duracion_inicial ? `${p.duracion_inicial} ${p.duracion_inicial === 1 ? 'hora' : 'horas'}` : 'Día completo';
    };

    // Confirmar reserva
    const handleConfirm = async () => {
        if (!selectedPlan || !selectedDate || !horaInicio || !selectedSucursal) return;
        if (!canSubmitSpaceReservation(
            selectedPlan.requiere_espacio,
            !!selectedEspacio,
            validatedAvailabilityContext,
            availabilityContext,
            selectedEspacio ? espaciosDisponibles[selectedEspacio.id] : undefined,
        )) {
            setError('Todavía no se pudo confirmar que el espacio esté disponible.'); return;
        }
        const submitRequestId = ++submitRequestRef.current;
        const submitIntent = intentRef.current;
        setSubmitting(true); setError(null);

        let fechaFin = selectedDate;
        if (!usaTemporizador) {
            const dur = selectedPlan.duracion_inicial || 1;
            const [hStr] = horaInicio.split(':');
            let hFin = parseInt(hStr, 10) + dur;
            if (hFin >= 24) {
                hFin -= 24;
                const fo = parseLocalDate(selectedDate);
                fo.setDate(fo.getDate() + 1);
                fechaFin = fmtDateWithoutTZ(fo);
            }
        }

        try {
            const solicitud = await crearSolicitudReserva({
                sucursal: selectedSucursal.id,
                precio_plan_servicio: selectedPlan.id,
                espacio_servicio: selectedPlan.requiere_espacio ? (selectedEspacio?.id ?? null) : null,
                fecha_inicio: selectedDate,
                fecha_fin: range?.to ? fmtDateWithoutTZ(range.to) : fechaFin,
                hora_inicio: `${horaInicio}:00`,
                hora_fin: usaTemporizador ? null : (horaFin ? `${horaFin}:00` : null),
                cantidad_periodos: cantidadPeriodos,
                dias_habiles: daySelectionType === 'business',
                cupon: cuponAplicado?.id ?? null,
                usar_horas_afiliado: usarHorasAfiliado,
            });
            if (!mountedRef.current || submitRequestId !== submitRequestRef.current || submitIntent.identity !== intentRef.current.identity || submitIntent.generation !== intentRef.current.generation) return;
            setSubmittedRequest(solicitud);
            setSubmitted(true); scrollTop();
        } catch (err: any) {
            if (!mountedRef.current || submitRequestId !== submitRequestRef.current || submitIntent.identity !== intentRef.current.identity || submitIntent.generation !== intentRef.current.generation) return;
            setError(err.message || 'Error al crear la reserva.');
        }
        finally {
            if (mountedRef.current && submitRequestId === submitRequestRef.current && submitIntent.identity === intentRef.current.identity && submitIntent.generation === intentRef.current.generation) setSubmitting(false);
        }
    };

    const resetForm = () => {
        setSubmittedRequest(null); setSubmitted(false); setStep(0); setSelectedSucursal(null);
        setSelectedCategoria(null); setSelectedServicio(null); setSelectedPlan(null); setSelectedEspacio(null);
        setSelectedDate(''); setRange(undefined); setHoraInicio(null); setHoraFin(null); setCantidadPeriodos(1);
        invalidateReservationIntent();
        setTotalPrice(0); limpiarCupon();
        setPersons(1); setUsaTemporizador(false);
    };

    const cfgActivo = selectedPlan ? planConfigs.find(c => c.id === selectedPlan.plan_duracion_config) : null;
    const includedDates = reservationDates(selectedDate, cfgActivo?.unidad === 'dia' ? cantidadPeriodos * (cfgActivo.dias || 1) : 1, cfgActivo?.unidad === 'dia' && daySelectionType === 'business');

    const reservationRows = [
        ['Sucursal',selectedSucursal?.nombre],
        ['Servicio',selectedServicio?.nombre],
        ['Espacio',selectedEspacio?.espacio_nombre],
        ['Plan',selectedPlan?.plan_nombre],
        ['Fecha',selectedDate ? formatDate(selectedDate) : undefined],
        ...(range?.to && range.to > (range.from || range.to) ? [['Hasta',formatDate(fmtDateWithoutTZ(range.to))]] : []),
        ['Horario',horaInicio ? `${horaInicio}${horaFin && !usaTemporizador ? ` – ${horaFin}` : ''}` : undefined],
    ];
    return <div ref={topRef} className="booking-page">
        <header className="booking-header">
            <a href="/midashboard" className="booking-back"><ChevronLeft size={16}/> Mi dashboard</a>
            <div className="booking-heading"><div><p className="booking-eyebrow">CO MARCA · RESERVA YA</p><h1>Reserva tu espacio.</h1><p>Elige dónde, cuándo y cómo quieres trabajar.</p></div><span className="booking-header-icon"><Building2 size={30}/></span></div>
        </header>
        <div className="booking-container">
        {error && <div className="booking-alert" role="alert"><span>{error}</span><button type="button" aria-label="Cerrar mensaje" onClick={()=>setError(null)}><X size={18}/></button></div>}
        {submitted && submittedRequest ? <ReservationRequestSuccess codigo={submittedRequest.codigo} whatsappUrl={submittedRequest.whatsapp_url} whatsappNumber={selectedSucursal?.telefono} serviceName={submittedRequest.servicio_nombre || selectedServicio?.nombre} branchName={submittedRequest.sucursal_nombre || selectedSucursal?.nombre} dateLabel={formatDate(submittedRequest.fecha_inicio || selectedDate)} timeLabel={submittedRequest.hora_inicio || horaInicio} onViewRequests={()=>navigate(ROUTES.solicitudesReserva)} onNewRequest={resetForm}/> : <>
            <nav className="booking-progress" aria-label="Pasos de la reserva">{STEPS.map((label,i)=><button type="button" key={label} disabled={i>step} aria-current={i===step?'step':undefined} onClick={()=>{setStep(i);scrollTop();}}><span>{i<step?<CheckCircle2 size={18}/>:i+1}</span><div><small>PASO {i+1}</small><strong>{label}</strong></div></button>)}</nav>
            <div className="booking-layout">
            <main key={step} className="booking-stage">
                <div className="booking-stage-heading"><span className="booking-eyebrow">PASO {step+1} DE 4</span><h2>{['¿Dónde quieres reservar?','Encuentra tu espacio','Elige la fecha y tu horario','Confirma tu reserva'][step]}</h2><p>{['Elige la ubicación que te resulte más cómoda.','Selecciona el servicio, tu espacio y el plan que necesitas.','Consulta la ocupación y elige la hora que mejor te viene.','Revisa los detalles antes de enviar tu solicitud.'][step]}</p></div>
                {step===0 && <div className="booking-cards">
                    {loadingSucursales && <p role="status">Cargando sucursales…</p>}
                    {sucursales.map(s=><button type="button" key={s.id} className={`booking-location ${selectedSucursal?.id===s.id?'is-selected':''}`} aria-pressed={selectedSucursal?.id===s.id} onClick={()=>selectSucursal(s)}><span className="booking-card-icon"><Building2 size={24}/></span><span className="booking-selection"><CheckCircle2 size={20}/></span><h3>{s.nombre}</h3><p><MapPin size={15}/>{s.direccion}</p>{s.telefono && <p><Phone size={15}/>{s.telefono}</p>}<span className="booking-card-link">{selectedSucursal?.id===s.id?'Sucursal seleccionada':'Elegir esta sucursal'}<ArrowRight size={16}/></span></button>)}
                    {!loadingSucursales && !sucursales.length && <p>No hay sucursales activas en este momento.</p>}
                </div>}
                {step===1 && <>
                    <section className="booking-section"><h3>¿Qué necesitas?</h3>{loadingCat?<p role="status">Cargando categorías…</p>:<div className="booking-categories">{categorias.map(cat=><button type="button" key={cat.id} aria-pressed={selectedCategoria?.id===cat.id} onClick={()=>selectCategoria(cat)}>{cat.nombre}</button>)}</div>}{!loadingCat && !categorias.length && <p>Sin categorías en esta sucursal.</p>}</section>
                    {selectedCategoria && <section className="booking-section"><h3>Elige el servicio</h3>{loadingServ && <p role="status">Cargando servicios…</p>}<div className="booking-cards">{servicios.map(serv=><button type="button" key={serv.id} className={`booking-service ${selectedServicio?.id===serv.id?'is-selected':''}`} aria-pressed={selectedServicio?.id===serv.id} onClick={()=>selectServicio(serv)}><span className="booking-card-icon"><Package size={22}/></span><h4>{serv.nombre}</h4><p>{serv.descripcion || 'Un lugar para trabajar a tu manera.'}</p><span className="booking-card-link">{selectedServicio?.id===serv.id?'Seleccionado':'Ver espacios y planes'}<ChevronRight size={16}/></span></button>)}</div>{!loadingServ&&!servicios.length&&<p>Sin servicios en esta categoría.</p>}</section>}
                    {selectedServicio?.requiere_espacio && (!selectedPlan || selectedPlan.requiere_espacio) && espacios.length>0 && <section className="booking-section"><h3>Elige tu espacio</h3><p>Selecciona el espacio que quieres reservar. Verás su agenda en el siguiente paso.</p><div className="booking-cards">{espacios.map(esp=><button type="button" key={esp.id} className={`booking-space ${selectedEspacio?.id===esp.id?'is-selected':''}`} aria-pressed={selectedEspacio?.id===esp.id} onClick={()=>{setSelectedEspacio(esp);setError(null);}}>{esp.espacio_imagen?<img src={esp.espacio_imagen} alt={esp.espacio_nombre}/>:<div className="booking-space-placeholder"><Building2 size={38}/></div>}<div className="booking-space-body"><h4>{esp.espacio_nombre}</h4><p><Users size={15}/> Hasta {esp.cantidad_personas} personas</p><span className="booking-card-link">{selectedEspacio?.id===esp.id?'Espacio seleccionado':'Seleccionar espacio'}<CheckCircle2 size={17}/></span></div></button>)}</div></section>}
                    {selectedServicio && <section className="booking-section"><h3>¿Por cuánto tiempo?</h3>{loadingPlanes && <p role="status">Cargando planes…</p>}<div className="booking-plans">{planes.map(plan=><button type="button" key={plan.id} aria-pressed={selectedPlan?.id===plan.id} className={selectedPlan?.id===plan.id?'is-selected':''} onClick={()=>selectPlan(plan)}><span className="booking-radio"/><div><h4>{plan.plan_nombre}</h4><p>{getDuracion(plan)}</p></div><strong>{plan.precio_inicial}<small> Bs.</small></strong></button>)}</div>{!loadingPlanes&&!planes.length&&<p>Sin planes para este servicio.</p>}</section>}
                </>}
                {step===2 && <>
                    {selectedEspacio && <div className="booking-chosen-space"><Building2 size={22}/><div><strong>{selectedEspacio.espacio_nombre}</strong><span>{selectedPlan?.plan_nombre} · Hasta {selectedEspacio.cantidad_personas} personas</span></div><button type="button" onClick={()=>{setStep(1);scrollTop();}}>Cambiar espacio</button></div>}
                    <div className="booking-date-layout"><section><h3 className="booking-field-title">1. Elige un día</h3><MiniCalendar initialMonth={parseLocalDate(`${calendarMonth}-01`)} selected={selectedDate} selectedDates={new Set(includedDates)} businessOnly={cfgActivo?.unidad==='dia' && daySelectionType==='business'} onSelect={date=>{setSelectedDate(date);setHoraInicio(null);setHoraFin(null);setCalendarMonth(date.slice(0,7));}} occupiedDates={monthlyOccupancy.occupiedDates} onMonthChange={setCalendarMonth}/><p className="booking-help">Los días naranjas tienen ocupación en algunos horarios.</p>{cfgActivo?.unidad==='dia' && <p className="booking-help" role="status">{includedDates.length ? `${includedDates.length} días incluidos en tu reserva. ` : ''}{daySelectionType==='business' ? 'De lunes a viernes; sábado y domingo no se cuentan.' : 'Incluye sábado y domingo.'} Los días marcados en rosa son los de tu reserva.</p>}{monthlyOccupancy.loading&&<p role="status" className="booking-help">Actualizando calendario…</p>}{monthlyOccupancy.error&&<p role="alert">{monthlyOccupancy.error}</p>}</section><section><h3 className="booking-field-title">2. Elige tu horario</h3>{selectedEspacio?<SpaceDaySchedule spaceId={selectedEspacio.id} branchId={selectedSucursal?.id} date={selectedDate} durationMinutes={usaTemporizador?15:Math.round((cfgActivo?.horas||selectedPlan?.duracion_inicial||1)*60)} selectedStart={horaInicio} onSelect={setHoraInicio}/>:<label className="booking-field">Hora de inicio<input type="time" value={horaInicio||''} onChange={e=>setHoraInicio(e.target.value)}/></label>}
                    {horaInicio && <div className="booking-time-summary"><Clock size={18}/><div><strong>{horaInicio}{horaFin&&!usaTemporizador?` → ${horaFin}`:''}</strong><span>{usaTemporizador?'La salida se registra al finalizar el uso.':'La hora de salida se calcula según tu plan.'}</span></div></div>}
                    {selectedEspacio&&selectedDate&&horaInicio&&<div className={`booking-availability ${selectedAvailability===false?'is-busy':''}`} role="status">{availabilityError || (selectedAvailability===true?'✓ Horario disponible':selectedAvailability===false?'Ese horario está ocupado. Elige otra hora.':'Comprobando tu horario…')}{availabilityError&&<button type="button" onClick={()=>void verificarDisponibilidad()}>Reintentar</button>}</div>}
                    </section></div>
                    <div className="booking-extra-fields">{cfgActivo?.unidad==='dia' && <><label className="booking-field">Cantidad de períodos<input type="number" min={1} value={cantidadPeriodos} onChange={e=>setCantidadPeriodos(Math.max(1,Math.floor(Number(e.target.value))||1))}/></label><label className="booking-field">Días de la reserva<select value={daySelectionType} onChange={e=>setDaySelectionType(e.target.value as 'all'|'business')}><option value="all">Todos los días</option><option value="business">Solo días hábiles</option></select></label></>}<label className="booking-field">Personas<input type="number" min={1} max={selectedEspacio?.cantidad_personas||20} value={persons} onChange={e=>setPersons(Math.max(1,Math.min(selectedEspacio?.cantidad_personas||20,Number(e.target.value)||1)))}/></label></div>
                </>}
                {step===3 && <>
                    <div className="booking-review"><span className="booking-card-icon"><Calendar size={24}/></span><h3>{selectedEspacio?.espacio_nombre||selectedServicio?.nombre}</h3><p>{selectedPlan?.plan_nombre} · {persons} persona{persons>1?'s':''}</p><dl>{reservationRows.filter(([label,value])=>value&&label!=='Espacio'&&label!=='Plan').map(([label,value])=><div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl><button type="button" className="booking-text-button" onClick={()=>{setStep(2);scrollTop();}}>Editar fecha y horario</button></div>
                    <section className="booking-section booking-coupon"><h3><Percent size={18}/> ¿Tienes un cupón?</h3>{cuponAplicado?<div className="booking-applied-coupon"><CheckCircle2 size={18}/><strong>{cuponAplicado.codigo}</strong><span>{cuponAplicado.descuento_monto?`-${cuponAplicado.descuento_monto} Bs.`:`-${cuponAplicado.descuento_porcentaje}%`}</span><button type="button" aria-label="Quitar cupón" onClick={limpiarCupon}><X size={18}/></button></div>:<div className="booking-coupon-input"><input aria-label="Código de cupón" placeholder="Ej: DESC10" value={cuponCodigo} onChange={e=>setCuponCodigo(e.target.value.toUpperCase())} onKeyDown={e=>e.key==='Enter'&&validarCupon()}/><button type="button" onClick={validarCupon} disabled={validandoCupon||!cuponCodigo.trim()}>{validandoCupon?'…':'Aplicar'}</button></div>}{cuponError&&<p role="alert">{cuponError}</p>}</section>
                    {miAfiliacion&&Number(miAfiliacion.horas_gratuitas_disponibles)>0&&<label className="booking-gift"><Gift size={24}/><div><strong>Horas de cortesía · {miAfiliacion.empresa_nombre}</strong><span>{formatGiftBalance(Number(miAfiliacion.horas_gratuitas_disponibles)*60)} disponibles este mes</span>{miAfiliacion.ciclo_actual_info&&<small>Vence: {miAfiliacion.ciclo_actual_info.fecha_fin}</small>}</div><input type="checkbox" aria-label="Solicitar uso de horas de cortesía" checked={usarHorasAfiliado} onChange={e=>setUsarHorasAfiliado(e.target.checked)}/></label>}
                </>}
                {step===3&&selectedEspacio&&(selectedAvailability===false||availabilityError)&&<div className="booking-alert" role="alert"><div><p>{availabilityError||'El horario elegido se ha ocupado mientras revisabas la reserva.'}</p><button type="button" className="booking-text-button" onClick={()=>{setStep(2);scrollTop();}}>Elegir otro horario</button></div></div>}
                <footer className="booking-actions"><button type="button" className="booking-previous" onClick={()=>{if(step===0)navigate(-1);else{setStep(s=>s-1);scrollTop();}}}><ChevronLeft size={17}/>{step===0?'Volver':'Anterior'}</button><button type="button" className="booking-next" disabled={!canNext()||submitting} onClick={()=>{if(step<3){setStep(s=>s+1);scrollTop();}else void handleConfirm();}}>{submitting?'Enviando solicitud…':step===3?'Confirmar reserva':'Continuar'}<ArrowRight size={18}/></button></footer>
            </main>
            <aside className={`booking-summary ${summaryOpen?'is-open':''}`} aria-label="Resumen de tu reserva"><span className="booking-eyebrow">TU RESERVA</span><h2>{selectedEspacio?.espacio_nombre||'Tu selección'}</h2><p className="booking-help">{selectedEspacio?'Todo lo que necesitas, en un solo lugar.':'Completa los pasos y revisa aquí tu selección.'}</p><button type="button" className="booking-summary-toggle" aria-expanded={summaryOpen} onClick={()=>setSummaryOpen(value=>!value)}>{summaryOpen?'Ocultar detalles':'Ver detalles de la reserva'}<ChevronRight size={16}/></button><dl className="booking-summary-details">{reservationRows.map(([label,value])=><div key={label}><dt>{label}</dt><dd>{value||'Por elegir'}</dd></div>)}</dl>{selectedPlan&&<div className="booking-total"><span>Total{usarHorasAfiliado?' con cortesía':''}</span>{cuponAplicado&&totalFinal<totalPrice&&<del>{totalPrice.toFixed(2)} Bs.</del>}<p><strong>{usarHorasAfiliado?'0.00':totalFinal.toFixed(2)}</strong><span> Bs.</span></p></div>}<div className="booking-summary-note"><Calendar size={18}/><p>Al confirmar enviaremos tu solicitud para su validación.</p></div></aside>
            </div>
        </>}
        </div>
    </div>;
};
export default ReservaYa;
